import { useState, FormEvent } from 'react';
import { supabase } from '@/lib/supabase';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openChat = () => {
    const chatBtn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (chatBtn) {
      chatBtn.click();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    const formEl = e.currentTarget as HTMLFormElement;
    const formDataObj = new FormData(formEl);

    const honeypot = formDataObj.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setStatusMsg('Thank you for your message. It has been received.');
      return;
    }

    formDataObj.delete('company_alt');

    // Save to Supabase first — this is what the admin panel reads from
    let supabaseError = false;
    try {
      const { error: supabaseErr } = await supabase.from('form_submissions').insert({
        name: formData.name || null,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject,
        message: formData.message || null,
      });
      if (supabaseErr) {
        console.error('Supabase form submission insert error:', supabaseErr);
        supabaseError = true;
      }
    } catch (err) {
      console.error('Supabase form submission insert exception:', err);
      supabaseError = true;
    }

    // Also submit to Readdy form API as secondary
    try {
      const res = await fetch('https://readdy.ai/api/form/d9fqud1gav9vgaom0k0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (!res.ok || parsed?.code !== 'OK') {
        console.warn('Readdy form API returned non-OK:', responseText);
      }
    } catch (err) {
      console.warn('Readdy form API call failed:', err);
    }

    if (supabaseError) {
      setStatus('error');
      setStatusMsg('Something went wrong sending your message. Please try again or contact us directly.');
    } else {
      setStatus('success');
      setStatusMsg('Thank you for your message. It has been received.');
      setFormData({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
      formEl.reset();
    }
  };

  return (
    <div className="bg-background-50">
      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
            / Get In Touch
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight max-w-3xl mb-6">
            Let's Start a{' '}
            <span className="text-primary-400">Conversation</span>
          </h1>
          <p className="text-foreground-300 text-base md:text-lg leading-relaxed max-w-2xl font-body">
            Whether you have questions about our services, want to partner with us, or need support — 
            our team is here to help.
          </p>

        </div>
      </section>

      {/* 24/7 Live Chat */}
      <section className="py-8 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={openChat}
            className="w-full group relative overflow-hidden rounded-2xl border border-primary-800/30 bg-background-100 p-8 md:p-10 text-left hover:border-primary-600/40 transition-all duration-300 cursor-pointer"
          >
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-14 h-14 md:w-16 md:h-16 flex-shrink-0 bg-primary-500/10 border border-primary-600/30 rounded-2xl flex items-center justify-center group-hover:bg-primary-500/15 transition-colors">
                <i className="ri-chat-3-line text-2xl md:text-3xl text-primary-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-2">
                  24/7 Live Chat with Us
                </h3>
                <p className="text-foreground-300 text-sm md:text-base max-w-xl font-body">
                  Need immediate help? Chat with our support team anytime, day or night. We're here to answer your questions about money transfers, account setup, bill payments, and more.
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full group-hover:bg-primary-400 transition-colors whitespace-nowrap">
                Start Chat
                <i className="ri-arrow-right-line" />
              </div>
            </div>
          </button>
        </div>
      </section>

      {/* Map Section — One Map, Three Global Hubs */}
      <section className="py-12 md:py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / Global Presence
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50 mb-4">
              Our Offices Across <span className="text-primary-400">Three Continents</span>
            </h2>
            <p className="text-foreground-300 text-sm md:text-base max-w-xl mx-auto font-body">
              Strategically positioned to serve MENA, European, and African markets with secure, compliant money transfer infrastructure.
            </p>
          </div>

          {/* Single Map Container */}
          <div className="relative rounded-2xl overflow-hidden border border-primary-800/25 bg-background-100 group">
            {/* Gradient overlays for depth */}
            <div className="absolute inset-0 bg-gradient-to-br from-background-100/15 via-transparent to-background-100/40 z-10 pointer-events-none" />

            <div className="relative w-full h-[420px] md:h-[520px]">
              <img
                src="https://storage.readdy-site.link/project_files/e63cb4d6-832c-48f6-a8a8-3d5a81b53170/757d0a9d-e906-4361-9914-2929f3d66bc5_compressed_ChatGPT-Image-Jul-20-2026-06_47_15-PM.webp"
                alt="TMT Global Network — Geneva, Beirut, Cairo"
                className="w-full h-full object-cover"
              />
              {/* Warm vignette overlays for seamless blending */}
              <div className="absolute inset-0 bg-gradient-to-t from-background-100/50 via-transparent to-background-100/10 z-10 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-b from-background-100/10 via-transparent to-background-100/30 z-10 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-r from-background-100/20 via-transparent to-background-100/20 z-10 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section id="contact-form" className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Form */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-6">
                Send Us a Message
              </h2>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                {/* Honeypot */}
                <div className="hp-field">
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="Your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body focus:outline-none focus:border-primary-500 transition-colors"
                    >
                      <option>General Inquiry</option>
                      <option>Partnership</option>
                      <option>Support</option>
                      <option>Business Account</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block font-label text-xs tracking-wider uppercase text-foreground-300 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-background-50 border border-primary-800/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    placeholder="How can we help you?"
                  />
                  <p className="text-foreground-600 text-xs mt-1 font-body">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-line" />
                    </>
                  )}
                </button>

                {status !== 'idle' && status !== 'submitting' && (
                  <p className={`text-sm font-body ${status === 'success' ? 'text-primary-400' : 'text-red-400'}`}>
                    {statusMsg}
                  </p>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="font-heading text-xl text-foreground-100 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 bg-primary-500/10 border border-primary-600/30 rounded-lg flex items-center justify-center">
                      <i className="ri-mail-line text-primary-400" />
                    </div>
                    <div>
                      <p className="font-label text-xs tracking-wider uppercase text-foreground-300 mb-1">Email</p>
                      <a href="mailto:service@trustedmoneytransfer.com" className="text-foreground-200 text-sm hover:text-primary-400 transition-colors font-body">
                        service@trustedmoneytransfer.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}